import type { SettledOverride } from "./orchestration";

const FORGE_LIFECYCLE_METADATA_KEY = "forgeLifecycle";
const FORGE_LIFECYCLE_SCHEMA_VERSION = 1;

export interface CmmdRuntimeThreadLifecycle {
  readonly settledAt: string | null;
  readonly settledOverride: SettledOverride;
}

const EMPTY_LIFECYCLE: CmmdRuntimeThreadLifecycle = Object.freeze({
  settledAt: null,
  settledOverride: null,
});

function isObject(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isIsoDateTime(value: unknown): value is string {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function isSettledOverride(value: unknown): value is SettledOverride {
  return value === null || value === "settled" || value === "active";
}

/**
 * Encodes Forge-only thread lifecycle fields into CMMD's versioned runtime
 * inventory metadata slot. CMMD stores and returns this slot without needing
 * to understand the runtime's lifecycle rules.
 */
export function encodeCmmdRuntimeThreadLifecycleMetadata(
  lifecycle: CmmdRuntimeThreadLifecycle,
): Readonly<Record<string, unknown>> {
  return {
    [FORGE_LIFECYCLE_METADATA_KEY]: {
      schemaVersion: FORGE_LIFECYCLE_SCHEMA_VERSION,
      settledAt: lifecycle.settledAt,
      settledOverride: lifecycle.settledOverride,
    },
  };
}

/** Reads only the exact versioned shape emitted by Forge runtimes. */
export function decodeCmmdRuntimeThreadLifecycleMetadata(
  metadata: Readonly<Record<string, unknown>> | null | undefined,
): CmmdRuntimeThreadLifecycle {
  if (!isObject(metadata)) return EMPTY_LIFECYCLE;
  const lifecycle = metadata[FORGE_LIFECYCLE_METADATA_KEY];
  if (!isObject(lifecycle) || lifecycle["schemaVersion"] !== FORGE_LIFECYCLE_SCHEMA_VERSION) {
    return EMPTY_LIFECYCLE;
  }

  const settledAt = lifecycle["settledAt"];
  const settledOverride = lifecycle["settledOverride"];
  if (!(settledAt === null || isIsoDateTime(settledAt)) || !isSettledOverride(settledOverride)) {
    return EMPTY_LIFECYCLE;
  }

  return { settledAt, settledOverride };
}
